import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";
const storageKey = "capsule-corp-theme";
const darkQuery = "(prefers-color-scheme: dark)";
const themeChangeEvent = "capsule-corp-theme-change";

function storedTheme(): Theme | null {
  try {
    const value = localStorage.getItem(storageKey);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}

function preferredTheme(): Theme {
  return storedTheme() ?? (window.matchMedia(darkQuery).matches ? "dark" : "light");
}

function applyTheme(theme: Theme) {
  document.documentElement.dataset.theme = theme;
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute("content", theme === "dark" ? "#071923" : "#075985");
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => document.documentElement.dataset.theme === "dark" ? "dark" : preferredTheme());

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    const media = window.matchMedia(darkQuery);
    const syncSystemTheme = () => { if (!storedTheme()) setThemeState(media.matches ? "dark" : "light"); };
    const syncStoredTheme = (event: StorageEvent) => { if (event.key === storageKey) setThemeState(preferredTheme()); };
    const syncAppTheme = (event: Event) => setThemeState((event as CustomEvent<Theme>).detail);
    media.addEventListener("change", syncSystemTheme);
    window.addEventListener("storage", syncStoredTheme);
    window.addEventListener(themeChangeEvent, syncAppTheme);
    return () => {
      media.removeEventListener("change", syncSystemTheme);
      window.removeEventListener("storage", syncStoredTheme);
      window.removeEventListener(themeChangeEvent, syncAppTheme);
    };
  }, []);

  const toggleTheme = useCallback(() => {
    const next = document.documentElement.dataset.theme === "dark" ? "light" : "dark";
    try { localStorage.setItem(storageKey, next); } catch { /* The theme still applies for this session. */ }
    applyTheme(next);
    setThemeState(next);
    window.dispatchEvent(new CustomEvent<Theme>(themeChangeEvent, { detail: next }));
  }, []);

  return { theme, toggleTheme };
}
